import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../../services/apiService';
import analyticsService from '../../services/analyticsService';
import type { ContestConfig } from '../../types';

export function useUserContests() {
  return useQuery({
    queryKey: ['contests', 'user'],
    queryFn: () => apiService.getUserContests(),
    staleTime: 0,
  });
}

export function useContests() {
  return useQuery({
    queryKey: ['contests'],
    queryFn: () => apiService.getContests(),
    staleTime: 0,
  });
}

export function useContestById(contestId: string | undefined) {
  return useQuery({
    queryKey: ['contests', contestId],
    queryFn: () => apiService.getContestById(contestId!),
    enabled: !!contestId,
    staleTime: 0,
  });
}

export function useContestLeaderboard(contestId: string | undefined, matchId?: number) {
  return useQuery({
    queryKey: ['contests', contestId, 'leaderboard', matchId ?? 'all'],
    queryFn: () => apiService.getContestLeaderboard(contestId!, matchId),
    enabled: !!contestId,
    staleTime: 60 * 1000, // 1 min — leaderboard doesn't change frequently
    placeholderData: (prev: any) => prev, // keep old data while refetching (no shimmer flicker)
  });
}

export function useContestConfig() {
  return useQuery({
    queryKey: ['contests', 'config'],
    queryFn: () => apiService.getContestConfig() as Promise<ContestConfig>,
  });
}

export function useCreateContest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (contestData: any) => apiService.createContest(contestData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['contests'] });
    },
  });
}

export function useContestLockStatus(contestId: string | undefined) {
  return useQuery({
    queryKey: ['contests', contestId, 'lock-status'],
    queryFn: () => apiService.getContestLockStatus(contestId!),
    enabled: !!contestId,
    staleTime: 0,
  });
}

export function useContestMembers(contestId: string | undefined) {
  return useQuery({
    queryKey: ['contests', contestId, 'members'],
    queryFn: () => apiService.getContestMembers(contestId!),
    enabled: !!contestId,
    staleTime: 0,
  });
}

export function useUpdateMemberStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contestId, memberId, status }: { contestId: string; memberId: string; status: string }) =>
      apiService.updateContestMemberStatus(contestId, memberId, { status }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['contests', variables.contestId, 'members'] });
      await queryClient.invalidateQueries({ queryKey: ['contests', variables.contestId] });
    },
  });
}

export function useRemoveContestMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contestId, memberId }: { contestId: string; memberId: string }) =>
      apiService.removeContestMember(contestId, memberId),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['contests', variables.contestId, 'members'] });
      await queryClient.invalidateQueries({ queryKey: ['contests', variables.contestId] });
      await queryClient.invalidateQueries({ queryKey: ['teams', variables.contestId] });
    },
  });
}

export function useJoinContest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ contestId, code }: { contestId: string; code?: string }) =>
      apiService.joinContest(contestId, code),
    onSuccess: async (_data, variables) => {
      analyticsService.track('contest_joined', { code: variables.code });
      await queryClient.invalidateQueries({ queryKey: ['contests', variables.contestId] });
      await queryClient.invalidateQueries({ queryKey: ['contests', 'user'] });
      await queryClient.invalidateQueries({ queryKey: ['contests'] });
    },
  });
}
